import { useMemo, useState } from "react";

import * as Cmp from "@impero-com/common/utils/comparison";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { Tag } from "@impero-com/ui";

import { useIsClamped } from "utils/hooks/useIsClamped";

import * as theme from "./TagsCell.css";
import { TagsDrawer } from "./TagsDrawer";

const MAX_TAGS = 30;

interface TagsCellProps {
    tags: Array<{ category: string; name: string }>;
}

export function TagsCell({ tags }: TagsCellProps) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [mounted, setMounted] = useState(false);
    const { ref: chipsRef, isClamped } = useIsClamped();
    const sortedTags = useMemo(() => {
        const compare = Cmp.string();
        return tags
            .map(({ category, name }) => ({
                id: JSON.stringify([category, name]),
                name,
                categoryName: category,
            }))
            .sort(
                (a, b) =>
                    compare(a.name, b.name) ||
                    compare(a.categoryName, b.categoryName)
            );
    }, [tags]);

    const openDrawer = () => {
        setMounted(true);
        setOpen(true);
    };

    if (tags.length === 0) {
        return null;
    }

    return (
        <div className={theme.cell}>
            <div ref={chipsRef} className={theme.chips}>
                {sortedTags.slice(0, MAX_TAGS).map((tag) => (
                    <Tag
                        key={tag.id}
                        className={theme.chip}
                        title={`${tag.categoryName}: ${tag.name}`}
                    >
                        <strong>{tag.categoryName}:</strong> {tag.name}
                    </Tag>
                ))}
            </div>
            {(isClamped || tags.length > MAX_TAGS) && (
                <Tag
                    variant="countPill"
                    className={theme.chip}
                    role="button"
                    tabIndex={0}
                    aria-label={t("ControlPrograms_TagsCell_ShowAll")}
                    onClick={openDrawer}
                    onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            openDrawer();
                        }
                    }}
                >
                    {tags.length}
                </Tag>
            )}
            {mounted && (
                <TagsDrawer
                    tags={sortedTags}
                    open={open}
                    onClose={() => setOpen(false)}
                />
            )}
        </div>
    );
}
